import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MoreVertical,
  FileText,
  Users,
  Edit,
  Activity,
  StickyNote,
  Paperclip,
  Calendar,
  MapPin,
  Building,
  DollarSign,
  Clock,
  User,
  ChevronDown,
  Filter,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase,
  Printer,
  Link,
  Pin,
  RefreshCw,
  X,
  Grid3X3,
  Star,
  ArrowUpDown,
  Lock,
  Unlock,
} from "lucide-react";
import { useToast } from "../components/shared/ToastProvider";
import ClientSubmissionModal from "../components/ClientSubmissionModal";
import AddSubmissionModal from "../components/AddSubmissionModal";
import AddButton from "../components/shared/AddButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JobView = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [job, setJob] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showClientSubmissionModal, setShowClientSubmissionModal] =
    useState(false);
  const [showAddSubmissionModal, setShowAddSubmissionModal] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [showCloseJobModal, setShowCloseJobModal] = useState(false);
  const [showReopenJobModal, setShowReopenJobModal] = useState(false);
  const [closeJobData, setCloseJobData] = useState({ reason: "", notes: "" });
  const [reopenJobData, setReopenJobData] = useState({ reason: "", notes: "" });

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
      fetchJobSubmissions();
    }
  }, [jobId]);

  useEffect(() => {
    if (activeTab === "submissions" && job) {
      fetchAIRecommendations();
    }
  }, [activeTab, job, jobId]);

  const handleClientSubmission = async (submissionData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/client-submissions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (response.ok) {
        showToast(
          `Successfully submitted ${submissionData.candidateIds.length} candidate(s) to client`,
          "success"
        );
        fetchJobSubmissions(); // Refresh submissions if needed
      } else {
        showToast("Failed to submit candidates to client", "error");
      }
    } catch (error) {
      console.error("Error submitting to client:", error);
      showToast("Error submitting candidates to client", "error");
    }
  };

  const handleSubmissionCreated = (newSubmission) => {
    showToast("Submission created successfully!", "success");
    fetchJobSubmissions(); // Refresh submissions
    setShowAddSubmissionModal(false);
  };

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsMenu && !event.target.closest(".relative")) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionsMenu]);

  const fetchJobDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}`
      );
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      } else {
        showToast("Failed to load job details", "error");
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      showToast("Error loading job details", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobSubmissions = async () => {
    try {
      setSubmissionsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/submissions/job/${jobId}`
      );
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error("Error fetching job submissions:", error);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const fetchAIRecommendations = async () => {
    if (!jobId) return;

    setRecommendationsLoading(true);
    try {
      // Use the new AI matching endpoint
      const response = await fetch(
        `${BACKEND_URL}/api/jobs/${jobId}/matched-candidates?limit=10`
      );

      if (response.ok) {
        const data = await response.json();
        // Transform the data to match the expected format
        const transformedRecommendations = (data.matched_candidates || []).map(
          (candidate) => ({
            id: candidate.candidate_id,
            name: candidate.candidate_name,
            title: candidate.current_title || "N/A",
            skills: candidate.skills || [],
            experience_years: candidate.total_experience_years || 0,
            match_score: candidate.match_score,
            match_confidence: candidate.match_confidence,
            match_breakdown: candidate.match_breakdown,
            match_reasons: candidate.match_reasons || [],
            match_reason:
              (candidate.match_reasons || [])[0] || "AI-matched candidate",
            source: "existing",
            location: candidate.location || "N/A",
            email: candidate.email,
            phone: candidate.phone,
            current_company: candidate.current_company,
          })
        );
        setAiRecommendations(transformedRecommendations);
        console.log(
          "✅ Loaded AI-matched recommendations:",
          transformedRecommendations.length
        );
      } else {
        console.error("Failed to fetch AI recommendations");
        setAiRecommendations([]);
      }
    } catch (error) {
      console.error("Error fetching AI recommendations:", error);
      setAiRecommendations([]);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const getMockRecommendations = () => {
    if (!job) return [];

    const jobTitle = job.title || "";
    if (
      jobTitle.toLowerCase().includes("full stack") ||
      jobTitle.toLowerCase().includes("developer")
    ) {
      return [
        {
          id: "candidate_1",
          name: "Sarah Chen",
          title: "Senior Full Stack Developer",
          skills: ["React", "Node.js", "Python", "AWS", "MongoDB"],
          experience_years: 6,
          match_score: 92,
          match_reason:
            "Extensive experience with modern full-stack technologies",
          source: "existing",
          location: "San Francisco, CA",
          email: "sarah.chen@example.com",
        },
        {
          id: "candidate_2",
          name: "Michael Rodriguez",
          title: "Full Stack Engineer",
          skills: ["JavaScript", "React", "Express", "PostgreSQL", "Docker"],
          experience_years: 4,
          match_score: 88,
          match_reason: "Strong frontend and backend development skills",
          source: "web",
          location: "Austin, TX",
          email: "m.rodriguez@example.com",
        },
        {
          id: "candidate_3",
          name: "Emily Johnson",
          title: "Software Developer",
          skills: ["Vue.js", "Python", "Django", "MySQL", "Git"],
          experience_years: 5,
          match_score: 85,
          match_reason: "Solid experience with web frameworks and databases",
          source: "existing",
          location: "Seattle, WA",
          email: "emily.johnson@example.com",
        },
        {
          id: "candidate_4",
          name: "David Kim",
          title: "Lead Developer",
          skills: ["Angular", "Node.js", "TypeScript", "Redis", "Kubernetes"],
          experience_years: 8,
          match_score: 90,
          match_reason: "Senior-level expertise with leadership experience",
          source: "web",
          location: "New York, NY",
          email: "david.kim@example.com",
        },
        {
          id: "candidate_5",
          name: "Jessica Martinez",
          title: "Full Stack Developer",
          skills: ["React", "FastAPI", "Python", "PostgreSQL", "GraphQL"],
          experience_years: 3,
          match_score: 82,
          match_reason: "Growing developer with relevant modern tech stack",
          source: "existing",
          location: "Denver, CO",
          email: "j.martinez@example.com",
        },
        {
          id: "candidate_6",
          name: "Robert Thompson",
          title: "Senior Software Engineer",
          skills: [
            "React Native",
            "Node.js",
            "MongoDB",
            "AWS",
            "Microservices",
          ],
          experience_years: 7,
          match_score: 89,
          match_reason:
            "Cross-platform development with cloud architecture experience",
          source: "web",
          location: "Chicago, IL",
          email: "r.thompson@example.com",
        },
      ];
    }

    return [
      {
        id: "candidate_1",
        name: "Alex Wilson",
        title: `Senior ${job.title}`,
        skills: [
          "Leadership",
          "Strategy",
          "Communication",
          "Project Management",
        ],
        experience_years: 6,
        match_score: 85,
        match_reason: "Strong professional background with relevant experience",
        source: "existing",
        location: "Los Angeles, CA",
        email: "alex.wilson@example.com",
      },
    ];
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submissionId}/status?new_status=${newStatus}&change_reason=Status updated from job view&changed_by=recruiter&changed_by_name=Current User`,
        { method: "POST" }
      );

      if (response.ok) {
        showToast(
          `Status updated to ${newStatus.replace("_", " ")}`,
          "success"
        );
        fetchJobSubmissions(); // Refresh submissions
      } else {
        showToast("Failed to update status", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", "error");
    }
  };

  const handleCloseJob = async () => {
    if (!closeJobData.reason) {
      showToast("Please select a closure reason", "warning");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/close`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          closure_reason: closeJobData.reason,
          closure_notes: closeJobData.notes,
          closed_by: "current_user@juno.com", // TODO: Replace with actual user
        }),
      });

      if (response.ok) {
        const result = await response.json();
        showToast("Job closed successfully", "success");
        setShowCloseJobModal(false);
        setCloseJobData({ reason: "", notes: "" });
        fetchJobDetails(); // Refresh job details
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to close job", "error");
      }
    } catch (error) {
      console.error("Error closing job:", error);
      showToast("Error closing job", "error");
    }
  };

  const handleReopenJob = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/reopen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reopened_by: "current_user@juno.com", // TODO: Replace with actual user
          reopen_reason: reopenJobData.reason,
          reopen_notes: reopenJobData.notes,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        showToast("Job reopened successfully", "success");
        setShowReopenJobModal(false);
        setReopenJobData({ reason: "", notes: "" });
        fetchJobDetails(); // Refresh job details
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to reopen job", "error");
      }
    } catch (error) {
      console.error("Error reopening job:", error);
      showToast("Error reopening job", "error");
    }
  };

  const addCandidateToJob = async (candidate) => {
    try {
      const submissionData = {
        candidate_id: candidate.id,
        job_id: jobId,
        status: "sourced",
        source: candidate.source || "ai_recommendation",
        match_score: candidate.match_score || 85,
        internal_notes: `AI-recommended candidate with ${
          candidate.match_score
        }% match score. ${candidate.match_reason || ""}`,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/submissions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (response.ok) {
        const result = await response.json();
        showToast(
          `Successfully added ${candidate.name} to job submissions`,
          "success"
        );

        // Refresh submissions to show the new addition
        fetchJobSubmissions();

        return result;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to add candidate to job");
      }
    } catch (error) {
      console.error("Error adding candidate to job:", error);
      const errorMessage = error.message || "Unknown error occurred";
      const candidateName = candidate?.name || "candidate";
      showToast(
        `Failed to add ${candidateName} to job: ${errorMessage}`,
        "error"
      );
      throw new Error(errorMessage);
    }
  };

  const handleActionClick = (actionType) => {
    setShowActionsMenu(false);

    switch (actionType) {
      case "prescreen":
        setShowAddSubmissionModal(true);
        break;
      case "submissions":
        setActiveTab("submissions");
        showToast("Navigated to submissions tab", "success");
        break;
      case "client_submission":
        setShowClientSubmissionModal(true);
        break;
      case "interview":
        showToast("Interview scheduling functionality coming soon", "info");
        break;
      case "offer_made":
        showToast("Offer extended functionality coming soon", "info");
        break;
      case "offer_accepted":
        showToast("Offer accepted functionality coming soon", "info");
        break;
      default:
        showToast("Action not implemented", "warning");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      sourced: "bg-blue-100 text-blue-800",
      screened: "bg-purple-100 text-purple-800",
      internally_submitted: "bg-yellow-100 text-yellow-800",
      technical_screened: "bg-indigo-100 text-indigo-800",
      client_accepted: "bg-green-100 text-green-800",
      client_reviewing: "bg-orange-100 text-orange-800",
      client_waiting: "bg-gray-100 text-gray-800",
      client_rejected: "bg-red-100 text-red-800",
      sendout_created: "bg-teal-100 text-teal-800",
      interview_scheduled: "bg-blue-100 text-blue-800",
      interview_scheduled: "bg-purple-100 text-purple-800",
      interview_scheduled: "bg-indigo-100 text-indigo-800",
      interview_scheduled: "bg-pink-100 text-pink-800",
      offer_made: "bg-emerald-100 text-emerald-800",
      offer_accepted: "bg-green-100 text-green-800",
      offer_rejected: "bg-red-100 text-red-800",
      placed: "bg-green-100 text-green-800",
      internally_rejected: "bg-red-100 text-red-800",
      candidate_not_interested: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      "\n" +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const StatusBadge = ({ status }) => (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
        status
      )}`}
    >
      {status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
    </span>
  );

  const filteredSubmissions = submissions.filter((submission) => {
    const statusMatch =
      statusFilter === "all" || submission.current_status === statusFilter;
    const sourceMatch =
      sourceFilter === "all" || submission.submission_source === sourceFilter;
    return statusMatch && sourceMatch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    // For demo purposes, create a mock job when job is not found
    const mockJob = {
      id: jobId,
      title: "Senior Full Stack Developer",
      description:
        "We are seeking a talented Senior Full Stack Developer to join our growing engineering team. The ideal candidate will have experience with modern web technologies, strong problem-solving skills, and a passion for building scalable applications.",
      required_skills: ["React", "Node.js", "Python", "AWS", "MongoDB"],
      location: "San Francisco, CA",
      experience_level: "Senior",
      salary_min: 120000,
      salary_max: 180000,
      employment_type: "Full-time",
      status: "Open",
    };

    // Set the mock job and continue rendering
    setTimeout(() => {
      setJob(mockJob);
      if (activeTab === "submissions") {
        fetchAIRecommendations();
      }
    }, 100);

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-lg">
                  <Briefcase className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {job.job_id || job.id}
                    </span>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {job.title}
                    </h1>
                    {job.status === "closed" && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-800 border border-red-300 rounded-full text-sm font-medium">
                        <Lock className="w-4 h-4" />
                        <span>CLOSED</span>
                      </span>
                    )}
                    {job.status === "on-hold" && (
                      <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-full text-sm font-medium">
                        ON HOLD
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center">
                      <Building className="w-4 h-4 mr-1" />G
                    </span>
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      in
                    </span>
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      📍
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <span className="font-medium">ACTIONS</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* Actions Dropdown Menu */}
                {showActionsMenu && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                    <button
                      onClick={() => handleActionClick("prescreen")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Users className="w-4 h-4" />
                      <span>Prescreen</span>
                    </button>

                    <button
                      onClick={() => handleActionClick("submissions")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4" />
                        <span>Submissions</span>
                      </div>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium border border-green-200">
                        {submissions.length}
                      </span>
                    </button>

                    <button
                      onClick={() => handleActionClick("client_submission")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Building className="w-4 h-4" />
                      <span>Client Submission</span>
                    </button>

                    <button
                      onClick={() => handleActionClick("interview")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Calendar className="w-4 h-4" />
                      <span>Interview</span>
                    </button>

                    <button
                      onClick={() => handleActionClick("offer_made")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Offer Extended</span>
                    </button>

                    <button
                      onClick={() => handleActionClick("offer_accepted")}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Offer-Accepted</span>
                    </button>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-2"></div>

                    {/* Close/Reopen Job */}
                    {job?.status === "closed" ? (
                      <button
                        onClick={() => {
                          setShowReopenJobModal(true);
                          setShowActionsMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center space-x-2"
                      >
                        <Unlock className="w-4 h-4" />
                        <span>Reopen Job</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setShowCloseJobModal(true);
                          setShowActionsMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <Lock className="w-4 h-4" />
                        <span>Close Job</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <Printer className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <Link className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <Pin className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate("/jobs")}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Job Details Row */}
          <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">ID</span>
              <div className="text-gray-900">{job.job_id || job.id}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Posting Title</span>
              <div className="text-gray-900">{job.title}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Employment Type</span>
              <div className="flex items-center space-x-1">
                <span className="text-gray-900">{job.employment_type}</span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status</span>
              <div className="flex items-center space-x-1">
                <span className="text-gray-900 capitalize">{job.status}</span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </div>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">
                Account/BD Manager
              </span>
              <div className="text-gray-900">
                {job.account_manager || job.hiring_manager || "Not assigned"}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 flex justify-between items-center">
          <nav className="flex space-x-8">
            {[
              { id: "overview", label: "OVERVIEW", icon: FileText },
              { id: "edit", label: "EDIT", icon: Edit },
              { id: "activity", label: "ACTIVITY", icon: Activity },
              { id: "notes", label: "NOTES", count: 5, icon: StickyNote },
              { id: "files", label: "FILES", count: 4, icon: Paperclip },
              {
                id: "submissions",
                label: "SUBMISSIONS",
                count: submissions.length,
                icon: Users,
              },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className="ml-1">({tab.count})</span>
                  )}
                </button>
              );
            })}
          </nav>

          <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium text-sm">
            <span>LAYOUT</span>
            <Grid3X3 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {activeTab === "overview" && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Job Overview
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Job Description
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {job.description}
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Required Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {job.required_skills?.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Experience Level
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {job.experience_level}
                  </p>
                </div>
                {job.salary_range && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Salary Range
                    </h4>
                    <p className="text-gray-600 text-sm">
                      ${job.salary_range.min?.toLocaleString()} - $
                      {job.salary_range.max?.toLocaleString()}{" "}
                      {job.salary_range.currency || "USD"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "submissions" && (
          <div className="space-y-6">
            {/* Web Response Section */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                    <Users className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="font-medium text-gray-900">
                    Web Response (0)
                  </span>
                </div>
              </div>
              <div className="p-6 text-center text-gray-500">
                <p>No web responses yet</p>
              </div>
            </div>

            {/* Submissions Section */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                      <Star className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="font-medium text-gray-900">
                      Submission ({submissions.length})
                    </span>
                    <span className="ml-8 text-sm font-medium text-gray-700">
                      LAST ACTIVITY
                    </span>
                    <span className="text-sm text-gray-900">07/02/2025</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowAddSubmissionModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Submit Candidate</span>
                    </button>
                    <div className="flex space-x-2">
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
                        Current
                      </button>
                      <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
                        All
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Recommendations Section */}
              {showRecommendations && (
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Star className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        AI-Recommended Candidates
                      </h3>
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        High Match Probability
                      </span>
                    </div>
                    <button
                      onClick={() =>
                        setShowRecommendations(!showRecommendations)
                      }
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {recommendationsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      <span className="text-gray-600">
                        Finding best candidate matches...
                      </span>
                    </div>
                  ) : aiRecommendations.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {aiRecommendations.slice(0, 6).map((candidate, index) => (
                        <div
                          key={candidate.id || index}
                          className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 text-base">
                                {candidate.name}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {candidate.title || "N/A"}
                              </p>
                              {candidate.current_company && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {candidate.current_company}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end space-y-1">
                              <span
                                className={`inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-bold rounded-full border ${
                                  candidate.match_score >= 80
                                    ? "bg-green-100 text-green-800 border-green-300"
                                    : candidate.match_score >= 60
                                    ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                    : "bg-orange-100 text-orange-800 border-orange-300"
                                }`}
                              >
                                {candidate.match_score >= 80 && (
                                  <Star className="w-3 h-3 fill-current" />
                                )}
                                <span>
                                  {Math.round(candidate.match_score)}%
                                </span>
                              </span>
                              {candidate.source === "web" && (
                                <span className="inline-flex px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                                  Web Sourced
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Match Reasons */}
                          {candidate.match_reasons &&
                            candidate.match_reasons.length > 0 && (
                              <div className="mb-3">
                                <div className="flex flex-wrap gap-1">
                                  {candidate.match_reasons
                                    .slice(0, 2)
                                    .map((reason, idx) => (
                                      <span
                                        key={idx}
                                        className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
                                      >
                                        {reason}
                                      </span>
                                    ))}
                                </div>
                              </div>
                            )}

                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">
                              Key Skills:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {(candidate.skills || [])
                                .slice(0, 3)
                                .map((skill, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                                  >
                                    {skill}
                                  </span>
                                ))}
                              {candidate.skills &&
                                candidate.skills.length > 3 && (
                                  <span className="text-xs text-gray-400">
                                    +{candidate.skills.length - 3} more
                                  </span>
                                )}
                            </div>
                          </div>

                          {/* Match Breakdown - Collapsible */}
                          {candidate.match_breakdown && (
                            <details className="mb-3">
                              <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                                View match breakdown
                              </summary>
                              <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Skills:</span>
                                  <span className="font-medium">
                                    {Math.round(
                                      candidate.match_breakdown.skill_match || 0
                                    )}
                                    %
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">
                                    Experience:
                                  </span>
                                  <span className="font-medium">
                                    {Math.round(
                                      candidate.match_breakdown
                                        .experience_match || 0
                                    )}
                                    %
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">
                                    Location:
                                  </span>
                                  <span className="font-medium">
                                    {Math.round(
                                      candidate.match_breakdown
                                        .location_match || 0
                                    )}
                                    %
                                  </span>
                                </div>
                              </div>
                            </details>
                          )}

                          <div className="flex space-x-2">
                            <AddButton
                              buttonId={`ai-rec-${candidate.id}`}
                              onAdd={() => addCandidateToJob(candidate)}
                              addText="Add to Job"
                              addingText="Adding..."
                              addedText="Added"
                              className="flex-1"
                              size="sm"
                              variant="primary"
                            />
                            <button
                              onClick={() => {
                                // View candidate profile
                                showToast(
                                  `Viewing ${candidate.name}'s profile...`,
                                  "info"
                                );
                              }}
                              className="bg-gray-100 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">
                        No AI recommendations available at this time
                      </p>
                      <button
                        onClick={fetchAIRecommendations}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <RefreshCw className="w-4 h-4 inline mr-1" />
                        Refresh Recommendations
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Submissions Table */}
              <div className="overflow-x-auto">
                {submissionsLoading ? (
                  <div className="p-6 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading submissions...</p>
                  </div>
                ) : filteredSubmissions.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p>No submissions found for this job</p>
                    {aiRecommendations.length > 0 && (
                      <p className="mt-2 text-sm">
                        Check out the AI recommendations above!
                      </p>
                    )}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input type="checkbox" className="rounded" />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            <span>Candidate</span>
                            <ArrowUpDown className="w-4 h-4" />
                            <Filter className="w-4 h-4" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            <span>Date Added</span>
                            <ArrowUpDown className="w-4 h-4" />
                            <Filter className="w-4 h-4" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            <span>Date Last Modified</span>
                            <ArrowUpDown className="w-4 h-4" />
                            <Filter className="w-4 h-4" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            <span>Status</span>
                            <ArrowUpDown className="w-4 h-4" />
                            <Filter className="w-4 h-4" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            <span>Added By</span>
                            <ArrowUpDown className="w-4 h-4" />
                            <Filter className="w-4 h-4" />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            <span>Source</span>
                            <ArrowUpDown className="w-4 h-4" />
                            <Filter className="w-4 h-4" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredSubmissions.map((submission) => (
                        <tr key={submission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input type="checkbox" className="rounded" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="relative group">
                                <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                                  ACTIONS ▼
                                </button>
                                <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                  <button
                                    onClick={() =>
                                      handleStatusChange(
                                        submission.id,
                                        "client_reviewing"
                                      )
                                    }
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Send to Client
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleStatusChange(
                                        submission.id,
                                        "interview_scheduled"
                                      )
                                    }
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Schedule Interview
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleStatusChange(
                                        submission.id,
                                        "offer_made"
                                      )
                                    }
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Extend Offer
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleStatusChange(
                                        submission.id,
                                        "placed"
                                      )
                                    }
                                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Mark as Placed
                                  </button>
                                </div>
                              </div>
                              <Eye className="w-4 h-4 text-blue-600" />
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="font-medium text-blue-600">
                                {submission.candidate_name || `Paul Ramireddy`}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>07/02/2025</div>
                            <div>2:52 PM</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>07/02/2025</div>
                            <div>3:44 PM</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="relative group">
                              <span className="inline-flex items-center">
                                <StatusBadge
                                  status={submission.current_status}
                                />
                                <ChevronDown className="w-4 h-4 ml-1" />
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {submission.recruiter_name || "Siva Kumar"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {submission.submission_source || "Manual"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              <div className="px-6 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      ITEMS PER PAGE:
                    </span>
                    <select className="text-sm border border-gray-300 rounded px-2 py-1">
                      <option>10</option>
                      <option>20</option>
                      <option>50</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 -ml-6 pointer-events-none" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      ‹
                    </button>
                    <span className="px-2 py-1 text-sm bg-gray-100 rounded">
                      1
                    </span>
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      ›
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab !== "overview" && activeTab !== "submissions" && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-center text-gray-500">
              <p>Content for {activeTab.toUpperCase()} tab coming soon...</p>
            </div>
          </div>
        )}
      </div>

      {/* Client Submission Modal */}
      {showClientSubmissionModal && (
        <ClientSubmissionModal
          isOpen={showClientSubmissionModal}
          onClose={() => setShowClientSubmissionModal(false)}
          onSubmit={handleClientSubmission}
          job={job}
        />
      )}

      {/* Add Submission Modal */}
      {showAddSubmissionModal && (
        <AddSubmissionModal
          isOpen={showAddSubmissionModal}
          onClose={() => setShowAddSubmissionModal(false)}
          selectedJob={job}
          onSubmissionCreated={handleSubmissionCreated}
        />
      )}

      {/* Close Job Modal */}
      {showCloseJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Close Job</h3>
              <button
                onClick={() => setShowCloseJobModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Closure Reason *
                </label>
                <select
                  value={closeJobData.reason}
                  onChange={(e) =>
                    setCloseJobData({ ...closeJobData, reason: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a reason</option>
                  <option value="position_filled">Position Filled</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="on_hold">On Hold</option>
                  <option value="budget_cut">Budget Cut</option>
                  <option value="requirements_changed">
                    Requirements Changed
                  </option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={closeJobData.notes}
                  onChange={(e) =>
                    setCloseJobData({ ...closeJobData, notes: e.target.value })
                  }
                  placeholder="Add any additional notes about closing this job..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Closing this job will prevent new
                  submissions. You can reopen it later if needed.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCloseJobModal(false);
                    setCloseJobData({ reason: "", notes: "" });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCloseJob}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Close Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Job Modal */}
      {showReopenJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Reopen Job
              </h3>
              <button
                onClick={() => setShowReopenJobModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {job?.closure_reason && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    <strong>Previously closed:</strong>{" "}
                    {job.closure_reason.replace("_", " ")}
                  </p>
                  {job.closure_notes && (
                    <p className="text-sm text-gray-600 mt-1">
                      {job.closure_notes}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Reopening (Optional)
                </label>
                <input
                  type="text"
                  value={reopenJobData.reason}
                  onChange={(e) =>
                    setReopenJobData({
                      ...reopenJobData,
                      reason: e.target.value,
                    })
                  }
                  placeholder="e.g., Candidate declined offer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={reopenJobData.notes}
                  onChange={(e) =>
                    setReopenJobData({
                      ...reopenJobData,
                      notes: e.target.value,
                    })
                  }
                  placeholder="Add any additional notes about reopening this job..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Note:</strong> Reopening will allow new submissions to
                  be created for this job.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowReopenJobModal(false);
                    setReopenJobData({ reason: "", notes: "" });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReopenJob}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Reopen Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobView;
